import React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'textarea' | 'select' | 'checkbox' | 'file' | string;
  invalid?: boolean;
  rows?: number | string;
  innerRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>;
}

export const Input: React.FC<InputProps> = ({
  type = 'text',
  placeholder,
  value,
  onChange,
  disabled = false,
  className = '',
  invalid = false,
  children,
  rows,
  innerRef,
  ...props
}) => {
  const baseClasses = 'w-full px-3 py-2 text-base border rounded-lg focus:outline-none focus:ring-2 transition duration-200 bg-white';
  const invalidClasses = invalid 
    ? 'border-[var(--color-danger)] focus:ring-[var(--color-danger)]/30 focus:border-[var(--color-danger)]' 
    : 'border-[var(--color-gray-300)] focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)]';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed bg-[var(--color-gray-100)]' : '';

  // For checkbox type, use different base classes
  if (type === 'checkbox') {
    const checkboxClasses = `w-4 h-4 rounded border-[var(--color-gray-300)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/30 focus:ring-2 ${disabledClasses} ${className || ''}`;
    return (
      <input
        type="checkbox"
        disabled={disabled}
        className={checkboxClasses}
        ref={innerRef as React.RefObject<HTMLInputElement>}
        onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    );
  }

  // For file type, use different styling
  if (type === 'file') {
    const fileClasses = `w-full text-base text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary)]/10 file:text-[var(--color-primary)] hover:file:bg-[var(--color-primary)]/20 ${className || ''}`;
    return (
      <input
        type="file"
        disabled={disabled}
        className={fileClasses}
        ref={innerRef as React.RefObject<HTMLInputElement>}
        onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    );
  }

  const combinedClasses = `${baseClasses} ${invalidClasses} ${disabledClasses} ${className || ''}`;

  if (type === 'select') {
    return (
      <select
        className={combinedClasses}
        disabled={disabled}
        value={value}
        onChange={onChange as React.ChangeEventHandler<HTMLSelectElement>}
        ref={innerRef as React.RefObject<HTMLSelectElement>}
        {...(props as React.SelectHTMLAttributes<HTMLSelectElement>)}
      >
        {children}
      </select>
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        className={combinedClasses}
        placeholder={placeholder}
        value={value}
        onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
        disabled={disabled}
        rows={rows ? Number(rows) : undefined}
        ref={innerRef as React.RefObject<HTMLTextAreaElement>}
        {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  }

  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
      disabled={disabled}
      className={combinedClasses}
      ref={innerRef as React.RefObject<HTMLInputElement>}
      {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
    />
  );
};
